import React, { createContext, useMemo, useState } from "react";

// Create the context
export const DateContext = createContext();

// Helpers
const toDayKey = (date) => {
  // Local calendar day → YYYY-MM-DD
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const fromDayKey = (dayKey) => {
  // "YYYY-MM-DD" → Date at local start-of-day
  const [y, m, d] = String(dayKey).split("-").map(Number);
  const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
  dt.setHours(0, 0, 0, 0);
  return dt;
};

const startOfLocalDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Create the provider component
export const DateProvider = ({ children }) => {
  // ✅ Single source of truth: the dayKey string
  const [selectedDayKey, setSelectedDayKeyState] = useState(() =>
    toDayKey(startOfLocalDay(new Date()))
  );

  const value = useMemo(() => {
    const dayDate = fromDayKey(selectedDayKey); // local midnight Date for UI/display

    return {
      // ✅ Source of truth
      selectedDayKey,

      // Setter accepts:
      // - "YYYY-MM-DD" (preferred)
      // - Date
      // - date-like string
      setSelectedDayKey: (next) => {
        if (typeof next === "string" && /^\d{4}-\d{2}-\d{2}$/.test(next)) {
          setSelectedDayKeyState(next);
          return;
        }
        const d = next instanceof Date ? next : new Date(next);
        setSelectedDayKeyState(toDayKey(startOfLocalDay(d)));
      },

      // Convenience aliases (optional but helps drop-in refactors)
      dayKey: selectedDayKey,

      // For components like react-datepicker that want a Date object
      selectedDate: dayDate,
      setSelectedDate: (next) => {
        const d = next instanceof Date ? next : new Date(next);
        setSelectedDayKeyState(toDayKey(startOfLocalDay(d)));
      },

      // Display
      dayLabel: dayDate.toDateString(), // "Wed Jan 28 2026"
    };
  }, [selectedDayKey]);

  return <DateContext.Provider value={value}>{children}</DateContext.Provider>;
};